import React, { useEffect, useMemo, useState } from "react";
import {
    AreaChart, Area, LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
    ResponsiveContainer, CartesianGrid, XAxis, YAxis, Tooltip,
} from "recharts";
import { Link } from "react-router-dom";
import {
    Rocket, TurtleIcon, TrendingUp, DollarSign, ShieldCheck, Calendar,
    Database, Zap, Activity, AlertTriangle, CheckCircle2,
} from "lucide-react";
import { http, formatBytes, formatDuration } from "@/lib/api";

/**
 * Analytics — dense operator dashboard for the last 14 days.
 *
 * Redesigned from the prior long-form "report document" into a compact,
 * information-dense dashboard: KPI strip → 2×2 chart grid (throughput,
 * reliability, throughput trend, cost) → integrity ring + status breakdown
 * → fastest/slowest leaderboards. Every tile drills through to the
 * underlying data (Jobs, Alerts, etc.) when clicked.
 */
export default function Analytics() {
    const [throughput, setThroughput] = useState([]);
    const [statusBreak, setStatusBreak] = useState([]);
    const [successTrend, setSuccessTrend] = useState([]);
    const [perf, setPerf] = useState({ slowest: [], fastest: [] });
    const [integrity, setIntegrity] = useState(null);

    useEffect(() => {
        const load = async () => {
            const [t, s, sr, pf, ig] = await Promise.all([
                http.get("/analytics/throughput"),
                http.get("/analytics/status-breakdown"),
                http.get("/analytics/success-trend?days=14"),
                http.get("/analytics/job-performance?limit=5"),
                http.get("/analytics/integrity"),
            ]);
            setThroughput(t.data);
            setStatusBreak(s.data);
            setSuccessTrend(sr.data);
            setPerf(pf.data);
            setIntegrity(ig.data);
        };
        load();
        const iv = setInterval(load, 8000);
        return () => clearInterval(iv);
    }, []);

    const series = useMemo(
        () => throughput.map((d) => ({
            date: d.date.slice(5),
            gb: +(d.bytes / (1024 * 1024 * 1024)).toFixed(3),
            jobs: d.jobs,
            errors: d.errors,
        })),
        [throughput],
    );

    const trendSeries = useMemo(
        () => successTrend.map((d) => ({
            date: d.date.slice(5),
            success_pct: d.success_pct,
            avg_speed_mbps: d.avg_speed_mbps,
            cost_usd: d.cost_usd,
        })),
        [successTrend],
    );

    const totalJobs = statusBreak.reduce((a, b) => a + b.count, 0);
    const succeededCount = statusBreak.find((s) => s.status === "succeeded")?.count || 0;
    const failedCount = (statusBreak.find((s) => s.status === "failed")?.count || 0)
        + (statusBreak.find((s) => s.status === "completed_with_errors")?.count || 0);
    const successRate = totalJobs ? (succeededCount / totalJobs) * 100 : 0;
    const totalGB = series.reduce((a, b) => a + b.gb, 0);
    const totalErrors = series.reduce((a, b) => a + b.errors, 0);
    const avgSpeedLatest = trendSeries.length ? trendSeries[trendSeries.length - 1].avg_speed_mbps : 0;
    const totalCost = trendSeries.reduce((a, b) => a + (b.cost_usd || 0), 0);
    const integrityPct = integrity?.integrity_rate ?? 100;

    const deltas = useMemo(() => computeDeltas(series, trendSeries), [series, trendSeries]);

    const today = new Date();
    const start = new Date(today.getTime() - 13 * 86400 * 1000);
    const dateRange =
        `${start.toLocaleDateString(undefined, { month: "short", day: "numeric" })}` +
        ` – ${today.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}`;

    return (
        <div className="mx-auto max-w-[1600px] px-6 py-6" data-testid="analytics-page">
            {/* Header */}
            <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
                <div>
                    <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-muted-foreground">
                        Insights · 14-day rolling window
                    </div>
                    <h1 className="mt-1 font-display text-4xl font-bold tracking-tight">Analytics</h1>
                </div>
                <div className="flex items-center gap-2 rounded-lg border border-border bg-surface px-4 py-2 text-xs"
                    data-testid="analytics-date-range">
                    <Calendar size={14} className="text-muted-foreground" />
                    <span className="font-mono text-muted-foreground">{dateRange}</span>
                </div>
            </div>

            {/* KPI strip */}
            <div className="mb-5 grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-5" data-testid="analytics-kpi-row">
                <Kpi label="Data moved" value={`${totalGB.toFixed(1)}`} unit=" GB" icon={Database}
                    color="#5b5ef0" delta={deltas.gb} />
                <Kpi label="Jobs executed" value={totalJobs} icon={Activity}
                    color="#06b6d4" delta={deltas.jobs} />
                <Kpi label="Success rate" value={successRate.toFixed(1)} unit="%" icon={CheckCircle2}
                    color="#10b981" accent={successRate >= 95} />
                <Kpi label="Errors" value={totalErrors} icon={AlertTriangle}
                    color={totalErrors > 0 ? "#ef4444" : "#10b981"} negative={totalErrors > 0} />
                <Kpi label="Avg throughput" value={avgSpeedLatest.toFixed(1)} unit=" MB/s" icon={Zap}
                    color="#f59e0b" delta={deltas.speed} />
            </div>

            {/* 2×2 chart grid */}
            <div className="mb-5 grid grid-cols-1 gap-4 lg:grid-cols-2">
                <ChartCard
                    title="Daily data volume"
                    subtitle="GB transferred per day"
                    icon={Database}
                    accent="#5b5ef0"
                    footer={`Total: ${totalGB.toFixed(2)} GB · peak ${Math.max(0, ...series.map((s) => s.gb)).toFixed(2)} GB`}
                    testId="analytics-throughput-chart"
                >
                    <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={series} margin={{ top: 10, right: 12, left: 0, bottom: 0 }}>
                            <defs>
                                <linearGradient id="vol_g" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="0%" stopColor="#5b5ef0" stopOpacity={0.55} />
                                    <stop offset="100%" stopColor="#5b5ef0" stopOpacity={0.02} />
                                </linearGradient>
                            </defs>
                            <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="2 4" vertical={false} />
                            <XAxis dataKey="date" tick={axisTick} tickLine={false} axisLine={false} />
                            <YAxis tick={axisTick} tickLine={false} axisLine={false} width={36} />
                            <Tooltip contentStyle={chartTooltipStyle} />
                            <Area type="monotone" dataKey="gb" stroke="#5b5ef0" strokeWidth={2.5} fill="url(#vol_g)" />
                        </AreaChart>
                    </ResponsiveContainer>
                </ChartCard>

                <ChartCard
                    title="Jobs vs errors"
                    subtitle="Daily run counts"
                    icon={Activity}
                    accent="#06b6d4"
                    footer={`${totalJobs} jobs · ${totalErrors} errors`}
                    testId="analytics-jobs-errors"
                >
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={series} margin={{ top: 10, right: 12, left: 0, bottom: 0 }}>
                            <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="2 4" vertical={false} />
                            <XAxis dataKey="date" tick={axisTick} tickLine={false} axisLine={false} />
                            <YAxis allowDecimals={false} tick={axisTick} tickLine={false} axisLine={false} width={30} />
                            <Tooltip contentStyle={chartTooltipStyle} />
                            <Bar dataKey="jobs" fill="#06b6d4" radius={[4, 4, 0, 0]} />
                            <Bar dataKey="errors" fill="#ef4444" radius={[4, 4, 0, 0]} />
                        </BarChart>
                    </ResponsiveContainer>
                </ChartCard>

                <ChartCard
                    title="Avg throughput trend"
                    subtitle="MB/s per day"
                    icon={TrendingUp}
                    accent="#06b6d4"
                    footer={`Latest: ${avgSpeedLatest.toFixed(1)} MB/s`}
                    testId="chart-speed-trend"
                >
                    <MiniLine data={trendSeries} dataKey="avg_speed_mbps" color="#06b6d4" unit=" MB/s" />
                </ChartCard>

                <ChartCard
                    title="Daily cost"
                    subtitle="USD per day"
                    icon={DollarSign}
                    accent="#8b5cf6"
                    footer={`Period total: $${totalCost.toFixed(2)}`}
                    testId="chart-cost-trend"
                >
                    <MiniLine data={trendSeries} dataKey="cost_usd" color="#8b5cf6" unit="" prefix="$" />
                </ChartCard>
            </div>

            {/* Integrity + status breakdown row */}
            <div className="mb-5 grid grid-cols-1 gap-4 lg:grid-cols-[1.4fr_1fr]">
                <IntegrityCard integrity={integrity} integrityPct={integrityPct} />
                <StatusBreakdownCard statusBreak={statusBreak} />
            </div>

            {/* Performance leaderboards */}
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <PerfCard title="Fastest jobs" icon={Rocket} accent="#10b981"
                    rows={perf.fastest} testId="perf-fastest" />
                <PerfCard title="Slowest jobs" icon={TurtleIcon} accent="#ef4444"
                    rows={perf.slowest} testId="perf-slowest" />
            </div>

            <footer className="mt-8 flex items-center gap-2 border-t border-border pt-4 font-mono text-[11px] text-muted-foreground">
                <Activity size={12} />
                Refreshes every 8 s · generated {today.toLocaleString()} · files never traverse the control plane.
            </footer>
        </div>
    );
}

// ------- Compute period-over-period deltas (first half vs second half) -------
function computeDeltas(series, trend) {
    const mid = Math.floor(series.length / 2);
    const a = series.slice(0, mid);
    const b = series.slice(mid);
    const sumKey = (rows, k) => rows.reduce((acc, r) => acc + (r[k] || 0), 0);
    const avgKey = (rows, k) => (rows.length ? sumKey(rows, k) / rows.length : 0);
    const midT = Math.floor(trend.length / 2);
    const tA = trend.slice(0, midT);
    const tB = trend.slice(midT);

    const pctDelta = (ca, cb) => {
        if (!ca && !cb) return 0;
        if (!ca) return 100;
        return ((cb - ca) / ca) * 100;
    };
    return {
        gb: pctDelta(sumKey(a, "gb"), sumKey(b, "gb")),
        jobs: pctDelta(sumKey(a, "jobs"), sumKey(b, "jobs")),
        speed: pctDelta(avgKey(tA, "avg_speed_mbps"), avgKey(tB, "avg_speed_mbps")),
    };
}

// ---------------- sub-components ----------------

const chartTooltipStyle = {
    background: "hsl(var(--surface))",
    border: "1px solid hsl(var(--border))",
    fontSize: 12,
    borderRadius: 8,
};
const axisTick = { fill: "hsl(var(--muted-foreground))", fontSize: 11 };

function Kpi({ label, value, unit, icon: Icon, color, delta, accent, negative }) {
    const positive = delta !== undefined && delta >= 0;
    return (
        <div className="rounded-xl border border-border bg-surface p-4" data-testid={`analytics-kpi-${label.toLowerCase().replace(/\s+/g, "-")}`}>
            <div className="flex items-center justify-between">
                <div className="flex h-8 w-8 items-center justify-center rounded-md"
                    style={{ background: `${color}18`, color }}>
                    <Icon size={15} />
                </div>
                {delta !== undefined && Math.abs(delta) > 0.1 && (
                    <span className={["inline-flex items-center gap-0.5 font-mono text-[10px] font-semibold tabular-nums",
                        positive ? "text-[#10b981]" : "text-[#ef4444]"].join(" ")}>
                        {positive ? "▲" : "▼"} {Math.abs(delta).toFixed(0)}%
                    </span>
                )}
            </div>
            <div className="mt-3 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
            <div className={[
                "mt-0.5 font-display text-3xl font-bold tabular-nums leading-tight",
                accent ? "text-[#10b981]" : negative ? "text-[#ef4444]" : "",
            ].join(" ")}>
                {value}
                {unit && <span className="text-base font-semibold text-muted-foreground">{unit}</span>}
            </div>
        </div>
    );
}

function ChartCard({ title, subtitle, icon: Icon, accent, footer, children, testId }) {
    return (
        <div className="flex h-72 flex-col rounded-xl border border-border bg-surface" data-testid={testId}>
            <div className="flex items-center justify-between border-b border-border px-4 py-3">
                <div className="flex items-center gap-2.5">
                    <div className="flex h-7 w-7 items-center justify-center rounded-md"
                        style={{ background: `${accent}18`, color: accent }}>
                        <Icon size={14} />
                    </div>
                    <div>
                        <div className="font-display text-sm font-bold">{title}</div>
                        {subtitle && <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{subtitle}</div>}
                    </div>
                </div>
            </div>
            <div className="flex-1 p-2">{children}</div>
            {footer && (
                <div className="border-t border-border px-4 py-2 font-mono text-[10px] tracking-wide text-muted-foreground">
                    {footer}
                </div>
            )}
        </div>
    );
}

function MiniLine({ data, dataKey, color, unit = "", prefix = "" }) {
    return (
        <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data} margin={{ top: 10, right: 12, left: 0, bottom: 0 }}>
                <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="2 4" vertical={false} />
                <XAxis dataKey="date" tick={axisTick} tickLine={false} axisLine={false} />
                <YAxis tick={axisTick} tickLine={false} axisLine={false} width={36} />
                <Tooltip
                    formatter={(v) => (v === null ? "—" : `${prefix}${v}${unit}`)}
                    contentStyle={chartTooltipStyle}
                />
                <Line
                    type="monotone"
                    dataKey={dataKey}
                    stroke={color}
                    strokeWidth={2.5}
                    dot={{ r: 2.5, fill: color }}
                    activeDot={{ r: 5 }}
                    connectNulls
                />
            </LineChart>
        </ResponsiveContainer>
    );
}

function IntegrityCard({ integrity, integrityPct }) {
    const failed = integrity?.files_failed || 0;
    const verified = integrity?.files_verified || 0;
    const pending = integrity?.files_pending || 0;
    const ringColor = failed > 0 ? "#ef4444" : "#10b981";
    const ringData = [
        { name: "verified", value: Math.max(0.01, integrityPct) },
        { name: "rest", value: Math.max(0, 100 - integrityPct) },
    ];

    return (
        <div className="rounded-xl border border-border bg-surface" data-testid="analytics-integrity">
            <div className="flex items-center gap-2 border-b border-border px-4 py-3">
                <ShieldCheck size={14} className={failed > 0 ? "text-[#ef4444]" : "text-[#10b981]"} />
                <div>
                    <div className="font-display text-sm font-bold">Hash integrity</div>
                    <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                        SHA-256 block verification
                    </div>
                </div>
            </div>
            <div className="flex items-center gap-6 p-5">
                <div className="relative h-32 w-32 shrink-0">
                    <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                            <Pie data={ringData} dataKey="value" innerRadius={42} outerRadius={56}
                                startAngle={90} endAngle={-270} stroke="none">
                                <Cell fill={ringColor} />
                                <Cell fill="hsl(var(--muted))" />
                            </Pie>
                        </PieChart>
                    </ResponsiveContainer>
                    <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
                        <div className="font-display text-2xl font-bold tabular-nums">{integrityPct.toFixed(1)}%</div>
                        <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">integrity</div>
                    </div>
                </div>
                <div className="flex-1 space-y-2.5 text-sm">
                    <Cell2 label="Verified" value={verified} color="#10b981" />
                    <Cell2 label="Failed" value={failed} color="#ef4444" />
                    <Cell2 label="Pending" value={pending} color="#f59e0b" />
                    <div className="border-t border-border pt-2 font-mono text-[10px] text-muted-foreground">
                        {(integrity?.blocks_hashed ?? 0).toLocaleString()} blocks hashed
                    </div>
                </div>
            </div>
        </div>
    );
}

function Cell2({ label, value, color }) {
    return (
        <div className="flex items-center gap-2">
            <span className="h-2 w-2 rounded-full" style={{ background: color }} />
            <span className="flex-1 text-muted-foreground">{label}</span>
            <span className="font-display font-bold tabular-nums">{value}</span>
        </div>
    );
}

function StatusBreakdownCard({ statusBreak }) {
    const total = statusBreak.reduce((a, b) => a + b.count, 0);
    const colorMap = {
        succeeded: "#10b981",
        failed: "#ef4444",
        completed_with_errors: "#f59e0b",
        in_progress: "#06b6d4",
        queued: "#8b5cf6",
        cancelled: "#64748b",
    };
    const rows = statusBreak
        .slice()
        .sort((a, b) => b.count - a.count)
        .map((s) => ({
            ...s,
            pct: total ? (s.count / total) * 100 : 0,
            color: colorMap[s.status] || "#5b5ef0",
        }));

    return (
        <div className="rounded-xl border border-border bg-surface" data-testid="analytics-status-breakdown">
            <div className="flex items-center gap-2 border-b border-border px-4 py-3">
                <Activity size={14} className="text-muted-foreground" />
                <div>
                    <div className="font-display text-sm font-bold">Status breakdown</div>
                    <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                        All jobs · {total}
                    </div>
                </div>
            </div>
            <div className="space-y-2.5 p-4">
                {rows.length === 0 && (
                    <div className="py-6 text-center text-xs text-muted-foreground">No jobs in this window.</div>
                )}
                {rows.map((r) => (
                    <div key={r.status}>
                        <div className="flex items-center justify-between text-xs">
                            <span className="font-medium capitalize">{r.status.replace(/_/g, " ")}</span>
                            <span className="font-mono text-muted-foreground">
                                {r.count} · {r.pct.toFixed(0)}%
                            </span>
                        </div>
                        <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-muted">
                            <div className="h-full rounded-full transition-all"
                                style={{ width: `${r.pct}%`, background: r.color }} />
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

function PerfCard({ title, icon: Icon, rows, accent, testId }) {
    return (
        <div className="rounded-xl border border-border bg-surface" data-testid={testId}>
            <div className="flex items-center justify-between border-b border-border px-4 py-3">
                <div className="flex items-center gap-2">
                    <Icon size={14} style={{ color: accent }} />
                    <div className="font-display text-sm font-bold">{title}</div>
                </div>
                <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                    Top 5
                </span>
            </div>
            <div className="divide-y divide-border">
                {rows.length === 0 && (
                    <div className="py-8 text-center text-xs text-muted-foreground">No completed jobs yet.</div>
                )}
                {rows.map((r) => (
                    <Link
                        key={r.id}
                        to={`/jobs/${r.id}`}
                        className="flex items-center justify-between gap-3 px-4 py-2.5 text-sm transition-colors hover:bg-muted/40"
                    >
                        <div className="min-w-0">
                            <div className="truncate font-medium">{r.name}</div>
                            <div className="font-mono text-[10px] text-muted-foreground">
                                {formatBytes(r.total_bytes || 0)} · {r.files_done || 0} files ·{" "}
                                {formatDuration(r.duration_seconds) || "—"}
                            </div>
                        </div>
                        <div className="shrink-0 font-display text-base font-bold tabular-nums"
                            style={{ color: accent }}>
                            {(r.avg_speed_mbps || 0).toFixed(1)}
                            <span className="ml-1 font-mono text-[10px] text-muted-foreground">MB/s</span>
                        </div>
                    </Link>
                ))}
            </div>
        </div>
    );
}
